import { stripQueryParams } from 'utils/url'
import { isValidSubscriptionType } from '../hooks/use-analytics-order-success'
import type { ValidSubscriptionType } from '../hooks/use-analytics-order-success'

type ClientSecrets = Partial<{
  setupIntentClientSecret: string | null
  paymentIntentClientSecret: string | null
  userId: string | null
  tax: string | null
  currency: string | null
  subscriptionType: ValidSubscriptionType | null
  paymentUseCase: 'payment-button' | null
  subscriptionId: string | null
  id: string | null
  isFreeTrialAutostartSuccess: boolean
  isSubscriptionAutostartSuccess: boolean
  name: string | null
  email: string | null
  city: string | null
  country: string | null
  line1: string | null
  line2: string | null
  postalCode: string | null
  state: string | null
  promoCode: string | null
  stripeAccount: string | null
  isFreeTrial: boolean
}>

const CLIENT_SECRET_QUERY_PARAMS = [
  'cf_currency',
  'cf_is_free_trial',
  'cf_subscription_id',
  'cf_subscription_type',
  'cf_payment_use_case',
  'cf_tax',
  'cf_user_id',
  'city',
  'country',
  'email',
  'is_free_trial_autostart_success',
  'is_subscription_autostart_success',
  'line1',
  'line2',
  'name',
  'payment_intent',
  'payment_intent_client_secret',
  'postalCode',
  'redirect_status',
  'setup_intent',
  'setup_intent_client_secret',
  'state',
  'cf_promo_code',
  'cf_stripe_account',
] as const

export function clearClientSecretsFromUrl() {
  if (typeof window === 'undefined') {
    return
  }

  const url = new URL(window.location.href)
  const hasClientSecrets = CLIENT_SECRET_QUERY_PARAMS.some(param => url.searchParams.has(param))

  if (!hasClientSecrets) {
    return
  }

  const nextUrl = stripQueryParams(url, [...CLIENT_SECRET_QUERY_PARAMS])
  window.history.replaceState(window.history.state, '', nextUrl.toString())
}

function getClientSecrets(): ClientSecrets {
  if (typeof window === 'undefined') {
    return {}
  }

  const urlParams = new URLSearchParams(window.location.search)

  const setupIntentClientSecret = urlParams.get('setup_intent_client_secret')
  const paymentIntentClientSecret = urlParams.get('payment_intent_client_secret')
  const userId = urlParams.get('cf_user_id')
  const tax = urlParams.get('cf_tax')
  const currency = urlParams.get('cf_currency')
  const subscriptionType = urlParams.get('cf_subscription_type')
  const paymentUseCase = urlParams.get('cf_payment_use_case')
  const subscriptionId = urlParams.get('cf_subscription_id')
  const id = urlParams.get('payment_intent') || urlParams.get('setup_intent')
  const name = urlParams.get('name')
  const email = urlParams.get('email')
  const city = urlParams.get('city')
  const country = urlParams.get('country')
  const line1 = urlParams.get('line1')
  const line2 = urlParams.get('line2')
  const postalCode = urlParams.get('postalCode')
  const state = urlParams.get('state')
  const promoCode = urlParams.get('cf_promo_code')
  const stripeAccount = urlParams.get('cf_stripe_account')
  const isFreeTrialParam = urlParams.get('cf_is_free_trial')
  const isFreeTrial = isFreeTrialParam === null ? undefined : isFreeTrialParam === 'true'
  //in case of free_trial_autostart
  const isFreeTrialAutostartSuccess = urlParams.get('is_free_trial_autostart_success')
  const isSubscriptionAutostartSuccess = urlParams.get('is_subscription_autostart_success')

  return {
    setupIntentClientSecret,
    paymentIntentClientSecret,
    userId,
    tax,
    currency,
    subscriptionType: isValidSubscriptionType(subscriptionType) ? subscriptionType : null,
    paymentUseCase: paymentUseCase === 'payment-button' ? paymentUseCase : null,
    subscriptionId,
    id,
    isFreeTrialAutostartSuccess: Boolean(isFreeTrialAutostartSuccess),
    isSubscriptionAutostartSuccess: Boolean(isSubscriptionAutostartSuccess),
    name,
    email,
    city,
    country,
    line1,
    line2,
    postalCode,
    state,
    promoCode,
    stripeAccount,
    isFreeTrial,
  }
}

export const clientSecrets = getClientSecrets()
